import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, BadgeCheck, Building2, Cpu, Eye, ListChecks, Scale } from "lucide-react";
import { Seo } from "../components/layout/Seo";
import { Button } from "../components/ui/Button";
import { LinkSurface } from "../components/ui/LinkSurface";
import { Section } from "../components/ui/Section";
import { TextLink } from "../components/ui/TextLink";
import { CorridorDiagram } from "../components/visualizations/CorridorDiagram";
import { HeroBackdrop } from "../components/visualizations/HeroBackdrop";
import { isLocale, localizedPath } from "../lib/routes";
import { track } from "../lib/analytics";

const moldovaItems = ["Energy costs", "Grid resilience", "Water & climate", "Production efficiency", "Sustainability"];
const israelItems = ["Market fit", "Use-case validation", "Localization", "Pilot development", "Local ecosystem"];

const processSteps = [
  ["Understand", "Define the real operational challenge."],
  ["Assess", "Build the technical and economic baseline."],
  ["Structure", "Match technology, expertise, regulation and financing pathways."],
  ["Pilot", "Test a solution in a controlled real-world setting where appropriate."],
  ["Measure & Scale", "Evaluate results before deciding what should happen next."]
];

const integrity = [
  { icon: Scale, title: "Independent assessment", text: "Projects begin with the client's needs and objective criteria." },
  { icon: ListChecks, title: "Transparent partner selection", text: "Technologies are evaluated against the requirements of each project." },
  { icon: Eye, title: "Clear roles & interests", text: "Institutional and commercial interests are disclosed and managed transparently." },
  { icon: BadgeCheck, title: "Evidence before claims", text: "Partnerships, projects and impact are published only when they can be verified." }
];

const researchThemes = [
  { title: "Energy resilience in Moldova's agri-food sector", text: "Storage, energy management and operational constraints across processing, cold storage and wineries." },
  { title: "Technology fit: Israeli climate-tech and Moldovan needs", text: "Where technologies may solve real market problems and what successful localization requires." },
  { title: "Green Wine Moldova", text: "Energy, water and climate resilience across the vineyard-to-winery value chain." }
];

const focusAreas = [
  { title: "Energy Resilience", slug: "energy-resilience", body: "Energy storage, management, efficiency and flexible systems for organizations seeking greater control over energy cost and reliability." },
  { title: "Climate-Smart Agriculture", slug: "climate-smart-agriculture", body: "Water efficiency, irrigation, agrivoltaics and technologies supporting agricultural resilience under changing climate conditions." },
  { title: "Sustainable Food & Wine", slug: "green-wine-moldova", body: "Energy, cooling, water, storage and process-efficiency challenges across wineries and food production." }
];

const corridorMoldova = [
  ["Energy transition", "High energy costs", "Climate exposure"],
  ["Agricultural modernization", "European market integration", "Water use"]
];

const corridorIsrael = [
  ["Energy & climate technology", "Water expertise", "Agricultural innovation"],
  ["Experience under constraints", "Market-ready use cases", "R&D proof"]
];

const wineStages: Array<[string, string, string[]]> = [
  ["01", "Vineyard", ["Water", "Climate", "Agrivoltaics"]],
  ["02", "Winery", ["Energy", "Cooling", "Storage", "Process efficiency"]],
  ["03", "Evidence", ["Resource use", "Energy data", "Sustainability"]],
  ["04", "Market", ["Resilience", "Competitiveness", "European readiness"]]
];

export function Home() {
  const { locale } = useParams();
  const activeLocale = isLocale(locale) ? locale : "en";

  return (
    <>
      <Seo title="From real challenges to implementation-ready projects" description="Transition Bridge connects Moldova's energy and climate needs with relevant technologies, expertise and financing pathways." />

      <section className="relative overflow-hidden pt-[calc(var(--header-height)+30px)]">
        <HeroBackdrop />
        <div className="container-grid relative z-10 grid items-center gap-10 pb-10 lg:grid-cols-[1.02fr_0.98fr] lg:gap-14">
          <div className="max-w-[720px]">
            <p className="eyebrow mb-5">MOLDOVA · ISRAEL · EUROPE</p>
            <p className="mb-6 flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.18em] text-ink-secondary">
              <span className="h-px w-8 bg-brand-blue/50" aria-hidden="true" />
              Technology · Finance · Implementation
            </p>
            <h1 className="text-balance text-[clamp(2.1rem,5.2vw,3.2rem)] font-extrabold leading-[1.04]">
              From real challenges to{" "}
              <span className="text-brand-blue">implementation-ready</span> projects.
            </h1>
            <p className="mt-6 max-w-[620px] text-lg leading-8 text-ink-secondary md:text-xl">
              Transition Bridge connects Moldova's energy and climate needs with relevant technologies, expertise and financing pathways, helping turn promising ideas into projects that can be evaluated, tested and scaled.
            </p>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <Button className="w-full sm:w-auto" to={localizedPath(activeLocale, "moldova")} onClick={() => track("hero_moldova_click")}>I have a project in Moldova</Button>
              <Button className="w-full sm:w-auto" to={localizedPath(activeLocale, "israel")} variant="secondary" onClick={() => track("hero_israel_click")}>I represent a technology company</Button>
            </div>
            <TextLink className="mt-5" to={localizedPath(activeLocale, "contact?flow=institution")} onClick={() => track("institution_path_click")}>
              For institutions &amp; funding partners
            </TextLink>
          </div>

          <div className="relative">
            <CorridorDiagram />
          </div>
        </div>
      </section>

      <Section eyebrow="THE MISSING LAYER" title="Start with the problem, not the equipment." intro="A viable project has to connect several things at once: a real operational need, reliable data, suitable technology, sound economics, regulation, financing and local implementation." className="section-tight-top">
        <div className="relative grid gap-10 lg:grid-cols-[1fr_0.82fr_1fr] lg:items-center">
          <GapSide title="A real need exists" body="A winery, farm, factory or community has an energy, climate or resource challenge." />
          <div className="relative">
            <div className="absolute inset-y-4 left-1/2 hidden w-px -translate-x-1/2 bg-brand-blue/20 lg:block" />
            <div className="relative mx-auto max-w-sm rounded-2xl border border-brand-blue/15 bg-brand-blue/[0.05] p-6">
              <p className="mb-5 text-center text-xs font-extrabold uppercase tracking-[0.16em] text-brand-blue">The missing layer</p>
              <ol className="grid gap-3">
                {["Feasibility", "Economics", "Financing", "Regulation", "Implementation"].map((item, index) => (
                  <li key={item} className="flex items-center gap-3">
                    <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-brand-blue/10 text-[10px] font-bold text-brand-blue">{index + 1}</span>
                    <span className="text-sm font-semibold text-ink-primary">{item}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>
          <GapSide title="A technology exists" body="Relevant technology and expertise may already exist." align="end" />
        </div>
        <div className="mt-10 grid gap-5 border-t border-line pt-9 md:grid-cols-[0.9fr_1.1fr] lg:items-center">
          <h3 className="text-balance text-3xl font-extrabold md:text-4xl">Transition Bridge works in that gap.</h3>
          <p className="max-w-2xl text-lg leading-8 text-ink-secondary">We begin with operating conditions and evidence. Technology selection comes later.</p>
        </div>
      </Section>

      <Section eyebrow="WHY THIS CORRIDOR" title="Different experience. Complementary needs." className="bg-bg-secondary">
        <div className="grid gap-8 lg:grid-cols-[1fr_140px_1fr] lg:items-stretch">
          <CorridorColumn side="moldova" title="Moldova" items={corridorMoldova}
            body="Moldovan businesses and communities are navigating changing energy economics, climate pressure and a broader transition toward European standards and investment models." />
          <div className="relative flex items-center justify-center">
            <div className="absolute inset-y-0 left-1/2 hidden w-px -translate-x-1/2 bg-line lg:block" />
            <div className="relative z-10 mx-auto flex w-full max-w-44 flex-col items-center gap-4 py-2 text-center">
              <span className="grid h-12 w-12 place-items-center">
                <img src="/branding/transition-bridge-mark.png" alt="Transition Bridge logo" width={48} height={48} className="h-12 w-12 object-contain" />
              </span>
              <p className="text-base font-extrabold leading-snug text-brand-blue">Adapt what works to where it is genuinely needed.</p>
            </div>
          </div>
          <CorridorColumn side="israel" title="Israel" items={corridorIsrael}
            body="Israeli companies and experts have developed technologies and operating experience in environments shaped by heat, water constraints, intensive agriculture and energy-system challenges." />
        </div>
      </Section>

      <Section eyebrow="CHOOSE YOUR PATH" title="Two structured entry points." intro="There is no fixed menu. A challenge, a technology or a programme are all valid starting points." className="section-tight-top">
        <div className="grid gap-6 lg:grid-cols-[1.05fr_0.95fr]">
          <PathCard
            index="01"
            to={localizedPath(activeLocale, "moldova")}
            icon={Building2}
            label="For organizations in Moldova"
            title="Turn an operational challenge into a viable project."
            body="For businesses, producers and communities exploring energy resilience, climate adaptation, efficiency or sustainable modernization."
            items={moldovaItems}
            cta="Explore Moldova"
            dark={false}
          />
          <PathCard
            index="02"
            to={localizedPath(activeLocale, "israel")}
            icon={Cpu}
            label="For technology companies"
            title="Build a credible route into Moldova."
            body="For Israeli technology companies seeking real local use cases, market validation and carefully structured pilot opportunities."
            items={israelItems}
            cta="Explore Israel"
            dark
          />
        </div>
      </Section>

      <Section eyebrow="HOW PROJECTS MOVE" title="From question to evidence." intro="The pathway is deliberately staged. Each step produces evidence before a decision is taken." className="bg-bg-secondary">
        <div className="relative grid gap-9 lg:grid-cols-5 lg:gap-6">
          <div className="absolute left-0 right-0 top-[15px] hidden h-px bg-line lg:block" aria-hidden="true" />
          {processSteps.map(([title, text], index) => (
            <div key={title} className="relative border-s border-line ps-5 lg:border-s-0 lg:ps-0">
              <span className="absolute -start-[13px] top-0 grid h-7 w-7 place-items-center rounded-full bg-bg-secondary text-[11px] font-bold text-brand-blue ring-1 ring-brand-blue/30 lg:start-0 lg:-top-[5px] lg:h-9 lg:w-9" aria-hidden="true">
                {String(index + 1).padStart(2, "0")}
              </span>
              <div className="lg:pt-8">
                <h3 className="text-lg font-extrabold uppercase">{title}</h3>
                <p className="mt-2 max-w-[30ch] text-sm leading-6 text-ink-secondary">{text}</p>
              </div>
            </div>
          ))}
        </div>
        <p className="mt-10 max-w-3xl border-s-2 border-brand-blue ps-5 text-ink-secondary">Not every assessment should become a project. Early evidence can also show when an idea is not yet viable.</p>
      </Section>

      <Section title="Focus areas" intro="Thematic modules where further assessment, feasibility and structured delivery can be useful.">
        <div className="grid gap-5 lg:grid-cols-3">
          {focusAreas.map((area, index) => (
            <FocusCard key={area.slug} index={String(index + 1).padStart(2, "0")} to={localizedPath(activeLocale, `programs/${area.slug}`)} title={area.title} body={area.body} />
          ))}
        </div>
        <Link className="mt-6 grid gap-4 rounded-2xl border border-line bg-surface p-6 transition-all duration-200 hover:-translate-y-0.5 hover:border-brand-blue/40 hover:shadow-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-[3px] focus-visible:ring-offset-bg-primary md:grid-cols-[200px_1fr_auto] md:items-center md:gap-8" to={localizedPath(activeLocale, "programs/energy-communities")}>
          <span className="eyebrow">EXPLORATORY PROGRAM</span>
          <span>
            <span className="block text-xl font-bold">Energy Communities</span>
            <span className="mt-1 block text-sm leading-6 text-ink-secondary">Research and pilot models for community-scale energy, shared infrastructure and local resilience.</span>
          </span>
          <span className="inline-flex items-center gap-2 text-sm font-bold text-brand-blue">Research &amp; pilot development <ArrowRight className="rtl-flip h-4 w-4" aria-hidden="true" /></span>
        </Link>
      </Section>

      <Section className="bg-brand-burgundySoft" eyebrow="FLAGSHIP PROGRAM · IN DEVELOPMENT" title="Green Wine Moldova" intro="From sustainable vineyard to sustainable bottle.">
        <p className="max-w-4xl text-lg leading-8 text-ink-secondary">
          Moldova's wine sector brings agriculture, water, climate exposure, refrigeration, energy use and European market requirements into one value chain. That makes it a strong environment for developing integrated transition models.
        </p>
        <div className="relative mt-12 grid gap-9 md:grid-cols-2 lg:grid-cols-4 lg:gap-6">
          <div className="absolute left-[11px] right-[11px] top-[13px] hidden h-px bg-brand-burgundy/25 lg:block" aria-hidden="true" />
          {wineStages.map(([number, title, items], index) => (
            <div key={title} className="relative border-s border-brand-burgundy/25 ps-6 lg:border-s-0 lg:ps-0">
              <span className="absolute -start-[13px] top-0 grid h-7 w-7 place-items-center rounded-full bg-brand-burgundySoft text-[10px] font-bold text-brand-burgundy ring-1 ring-brand-burgundy/30 lg:start-0 lg:-top-[10px]" aria-hidden="true">
                {number}
              </span>
              <div className="lg:pt-7">
                <h3 className="text-xl font-extrabold uppercase text-brand-burgundy">{title}</h3>
                <ul className="mt-4 grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm font-semibold text-brand-burgundy/80">
                  {items.map((item, i) => <li key={item} className={index === 3 && i >= items.length - 1 ? "lg:pr-6" : ""}>{item}</li>)}
                </ul>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center">
          <Button to={localizedPath(activeLocale, "programs/green-wine-moldova")} onClick={() => track("green_wine_cta")}>Explore Green Wine Moldova</Button>
          <p className="max-w-xl text-sm leading-6 text-ink-secondary">Specific pilots and partners will be published only after formal confirmation.</p>
        </div>
      </Section>

      <Section dark title="Built for long-term trust." intro="Long-term cooperation between businesses, technology providers and institutions requires clear roles, transparent decisions and evidence-based claims.">
        <div className="grid overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] sm:grid-cols-2 sm:divide-x sm:divide-y sm:divide-white/10">
          {integrity.map(({ icon: Icon, title, text }) => (
            <div key={title} className="p-8 md:p-10">
              <Icon className="mb-6 h-7 w-7 text-brand-cyan" aria-hidden="true" />
              <h3 className="mb-3 text-xl font-bold text-brand-darkText">{title}</h3>
              <p className="max-w-md text-sm leading-6 text-brand-darkMuted">{text}</p>
            </div>
          ))}
        </div>
        <Button className="mt-8" variant="darkSecondary" to={localizedPath(activeLocale, "transparency")}>Governance &amp; Transparency</Button>
      </Section>

      <Section title="Research agenda" intro="We are building a practical evidence base around the questions that determine whether sustainable technologies can move from interest to implementation.">
        <div className="mb-7 flex flex-wrap items-center gap-3">
          <span className="inline-flex rounded-full border border-line bg-surface px-3 py-1 text-xs font-bold uppercase text-ink-secondary">Research in development</span>
          <span className="text-sm text-ink-secondary">Published through Insights as evidence is verified.</span>
        </div>
        <ol className="border-t border-line">
          {researchThemes.map((item, index) => (
            <li key={item.title} className="grid gap-4 border-b border-line py-7 md:grid-cols-[120px_1fr_auto] md:gap-8">
              <span className="text-4xl font-extrabold text-brand-blue/40">{String(index + 1).padStart(2, "0")}</span>
              <div>
                <h3 className="text-xl font-bold">{item.title}</h3>
                <p className="mt-3 max-w-3xl leading-7 text-ink-secondary">{item.text}</p>
              </div>
              <span className="hidden items-center gap-2 text-sm font-semibold text-brand-blue md:inline-flex">In development <ArrowRight className="rtl-flip h-4 w-4" aria-hidden="true" /></span>
            </li>
          ))}
        </ol>
        <Button className="mt-8" variant="secondary" to={localizedPath(activeLocale, "insights")}>Explore Insights</Button>
      </Section>

      <Section title="Where are you starting from?" intro="A challenge, a technology or a programme can all be valid starting points. The first step is understanding whether there is a credible basis for moving forward." className="bg-bg-secondary">
        <div className="grid gap-3 md:grid-cols-3">
          {[
            ["I have a challenge in Moldova", "moldova"],
            ["I represent a technology company", "israel"],
            ["I represent an institution", "institution"]
          ].map(([label, flow]) => (
            <Button key={flow} to={localizedPath(activeLocale, `contact?flow=${flow}`)} variant={flow === "moldova" ? "primary" : flow === "israel" ? "dark" : "secondary"}>
              {label}
            </Button>
          ))}
        </div>
      </Section>
    </>
  );
}

function GapSide({ title, body, align = "start" }: { title: string; body: string; align?: "start" | "end" }) {
  return (
    <div className={`border-t-2 border-line pt-6 ${align === "end" ? "lg:text-end" : ""}`}>
      <h3 className="mb-4 text-2xl font-extrabold uppercase tracking-tight">{title}</h3>
      <p className={`max-w-md leading-7 text-ink-secondary ${align === "end" ? "lg:ms-auto" : ""} lg:max-w-none`}>{body}</p>
    </div>
  );
}

function CorridorColumn({ side, title, items, body, align = "start" }: { side: "moldova" | "israel"; title: string; items: string[][]; body: string; align?: "start" | "end" }) {
  const dot = side === "israel" ? "bg-brand-cyan" : "bg-brand-blue";
  return (
    <div className={`${align === "end" ? "lg:text-end" : ""}`}>
      <h3 className="border-b border-line pb-5 text-3xl font-extrabold uppercase tracking-tight">{title}</h3>
      <ul className="my-6 grid gap-x-8 gap-y-2.5 text-sm font-semibold text-ink-primary sm:grid-cols-2">
        {items.flat().map((item) => (
          <li key={item} className={`flex items-center gap-2.5 ${align === "end" ? "lg:justify-end" : ""}`}>
            <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${dot}`} aria-hidden="true" />
            {item}
          </li>
        ))}
      </ul>
      <p className={`max-w-xl leading-7 text-ink-secondary ${align === "end" ? "lg:ms-auto" : ""}`}>{body}</p>
    </div>
  );
}

function PathCard({ index, to, icon: Icon, label, title, body, items, cta, dark = false }: { index: string; to: string; icon: typeof Building2; label: string; title: string; body: string; items: string[]; cta: string; dark?: boolean }) {
  return (
    <Link className={`group relative block overflow-hidden rounded-3xl border p-8 transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-[3px] focus-visible:ring-offset-bg-primary hover:-translate-y-0.5 hover:shadow-soft md:p-10 lg:min-h-[440px] ${dark ? "border-brand-dark bg-gradient-to-br from-brand-darkSecondary to-brand-dark text-brand-darkText hover:border-brand-cyan/50 focus-visible:ring-offset-brand-dark" : "border-line bg-gradient-to-br from-surface to-bg-secondary text-ink-primary hover:border-brand-blue/40"}`} to={to}>
      <span className={`mb-7 block font-display text-sm font-bold ${dark ? "text-brand-cyan" : "text-brand-blue"}`}>{index}</span>
      <Icon className={`mb-8 h-9 w-9 ${dark ? "text-brand-cyan" : "text-brand-blue"}`} />
      <p className={`mb-4 ${dark ? "eyebrow-sage" : "eyebrow"}`}>{label}</p>
      <h2 className="max-w-md text-3xl font-bold leading-tight">{title}</h2>
      <p className={`mt-4 max-w-md leading-7 ${dark ? "text-brand-darkText/72" : "text-ink-secondary"}`}>{body}</p>
      <div className="mt-7 flex flex-wrap gap-2">
        {items.map((item) => (
          <span key={item} className={`rounded-lg px-3 py-2 text-xs font-semibold ${dark ? "border border-white/12 bg-white/5 text-brand-darkText/80" : "border border-line bg-bg-secondary text-ink-secondary"}`}>{item}</span>
        ))}
      </div>
      <span className={`mt-9 inline-flex items-center gap-2 font-bold ${dark ? "text-brand-cyan" : "text-brand-blue"}`}>{cta} <ArrowRight className="rtl-flip h-4 w-4 transition-transform duration-200 group-hover:translate-x-1 group-focus-visible:translate-x-1" aria-hidden="true" /></span>
    </Link>
  );
}

function FocusCard({ index, to, title, body }: { index: string; to: string; title: string; body: string }) {
  return (
    <LinkSurface className="min-h-72 p-8" to={to}>
      <div className="mb-10 flex items-center justify-between">
        <span className="block h-1 w-16 bg-brand-blue transition-all duration-300 group-hover:w-24 group-focus-visible:w-24" />
        <span className="font-display text-sm font-bold text-brand-blue/70">{index}</span>
      </div>
      <h3 className="mb-4 text-2xl font-bold">{title}</h3>
      <p className="leading-7 text-ink-secondary">{body}</p>
    </LinkSurface>
  );
}
